import {useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Fact as Stamp} from '@/components/fact'
import {FieldError} from '@/components/field-error'
import {
  mintLicenciaUploadTarget,
  useSaveMyLicencia,
  type LicenciaCategoria,
  type MyLicencia,
} from '@/hooks/inci/consola/use-licencia'
import {EVIDENCE_ACCEPT, uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {useLicenciaFieldErrors} from '@/hooks/common/use-licencia-field-errors'
import {useLicenciaFilePicker} from '@/hooks/common/use-licencia-file-picker'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const LICENCIA_CATEGORIAS: LicenciaCategoria[] = ['tecnico', 'tecnologo', 'profesional', 'posgrado']

function licenciaEstadoTone(estado: MyLicencia['estado']): 'green' | 'amber' | 'red' | 'neutral' {
  if (estado === 'verificada') return 'green'
  if (estado === 'rechazada') return 'red'
  if (estado === 'por_verificar') return 'amber'
  return 'neutral'
}

function LicenciaSstForm({licencia, onDone}: {licencia: MyLicencia; onDone: () => void}) {
  const {t} = useTranslation()
  const save = useSaveMyLicencia()
  const fileRef = useRef<HTMLInputElement>(null)
  const [numero, setNumero] = useState(licencia.numero ?? '')
  const [categoria, setCategoria] = useState<LicenciaCategoria>(licencia.categoria ?? 'tecnico')
  const [vigencia, setVigencia] = useState(licencia.vigencia ?? '')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const licenciaFields = useLicenciaFieldErrors()
  const fileField = useLicenciaFilePicker(setError)

  const validateForm = () => licenciaFields.validate(numero, vigencia)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errors = validateForm()
    if (errors.numero || errors.vigencia) return
    if (!fileField.file) {
      setError(t('licencia.fileRequired'))
      return
    }
    setUploading(true)
    setError(null)
    try {
      const target = await mintLicenciaUploadTarget('/me/licencia', fileField.file)
      await uploadToStorage(target, fileField.file, () => {})
      await save.mutateAsync({numero: numero.trim(), categoria, vigencia, doc_key: target.object_key})
      onDone()
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setUploading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 space-y-4" data-testid="licencia-form" noValidate>
      <div className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="licencia-numero">{t('licencia.numero')}</Label>
          <Input
            id="licencia-numero"
            className="font-mono"
            value={numero}
            onChange={(e) => {
              setNumero(e.target.value)
              licenciaFields.clearNumero()
            }}
            onBlur={licenciaFields.blurNumero}
            maxLength={tope('LIC')}
            required
            aria-invalid={!!licenciaFields.fieldErrors.numero}
          />
          <FieldError error={licenciaFields.fieldErrors.numero} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="licencia-categoria">{t('licencia.categoria')}</Label>
          <select
            id="licencia-categoria"
            data-testid="licencia-categoria"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value as LicenciaCategoria)}
            className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
            {LICENCIA_CATEGORIAS.map((c) => (
              <option key={c} value={c}>
                {t(`licencia.categorias.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="licencia-vigencia">{t('licencia.vigencia')}</Label>
          <Input
            id="licencia-vigencia"
            type="date"
            className="max-w-45 font-mono"
            value={vigencia}
            onChange={(e) => {
              setVigencia(e.target.value)
              licenciaFields.clearVigencia()
            }}
            onBlur={licenciaFields.blurVigencia}
            required
            aria-invalid={!!licenciaFields.fieldErrors.vigencia}
          />
          <FieldError error={licenciaFields.fieldErrors.vigencia} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>{t('licencia.fileLabel')}</Label>
        <input
          ref={fileRef}
          type="file"
          accept={EVIDENCE_ACCEPT}
          hidden
          data-testid="licencia-file-input"
          onChange={(e) => fileField.pickFile(e.target.files?.[0])}
        />
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
            {t('licencia.attach')}
          </Button>
          {fileField.file && <span className="max-w-56 truncate text-xs font-medium">{fileField.file.name}</span>}
        </div>
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={uploading}>
          {uploading ? t('licencia.uploading') : t('licencia.save')}
        </Button>
        {licencia.estado !== null && (
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>
            {t('licencia.cancel')}
          </Button>
        )}
      </div>
    </form>
  )
}

/** The caller's own `licencia_sst` (Res. 908/2025), a person-level credential; «Mi cuenta» is
 * the only place an owner reaches it. */
export function LicenciaSstDetail({licencia}: {licencia: MyLicencia}) {
  const {t} = useTranslation()
  const [editing, setEditing] = useState(false)
  const hasLicencia = licencia.estado !== null

  if (!hasLicencia || editing) {
    return (
      <>
        {!hasLicencia && <p className="mt-1 text-xs text-muted-foreground">{t('licencia.emptyHint')}</p>}
        <LicenciaSstForm licencia={licencia} onDone={() => setEditing(false)} />
      </>
    )
  }

  return (
    <div className="mt-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Tag tone={licenciaEstadoTone(licencia.estado)}>{t(`licencia.estado.${licencia.estado}`)}</Tag>
        {licencia.drifted && <Tag tone="amber">{t('licencia.driftedTag')}</Tag>}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Stamp label={t('licencia.numero')}>
          <span className="font-mono">{licencia.numero}</span>
        </Stamp>
        <Stamp label={t('licencia.categoria')}>
          {licencia.categoria && t(`licencia.categorias.${licencia.categoria}`)}
        </Stamp>
        <Stamp label={t('licencia.vigencia')}>
          <span className="font-mono">{licencia.vigencia}</span>
        </Stamp>
      </div>
      {licencia.estado === 'rechazada' && licencia.rechazo_motivo && (
        <p className="tone-red rounded-lg px-3 py-2 text-xs" data-testid="licencia-rechazo-motivo">
          {t('licencia.rejectedNotice', {motivo: licencia.rechazo_motivo})}
        </p>
      )}
      {licencia.drifted && (
        <p className="notice-amber rounded-lg px-3 py-2 text-xs" data-testid="licencia-drifted-notice">
          {t('licencia.driftedNotice')}
        </p>
      )}
      {licencia.download_url && (
        <a
          href={licencia.download_url}
          target="_blank"
          rel="noreferrer"
          data-testid="licencia-download"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
          <span aria-hidden="true">↓</span> {t('licencia.viewDocument')}
        </a>
      )}
      <div>
        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="licencia-update-open">
          {t('licencia.update')}
        </Button>
      </div>
    </div>
  )
}
